import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "page-shell" }
const _hoisted_2 = { class: "result-info" }
const _hoisted_3 = {
  key: 0,
  class: "result-error"
}
const _hoisted_4 = {
  key: 1,
  class: "fetching-state"
}
const _hoisted_5 = {
  key: 2,
  class: "inquiry-list"
}
const _hoisted_6 = { class: "card-left" }
const _hoisted_7 = { class: "card-copy" }
const _hoisted_8 = {
  key: 0,
  class: "card-id"
}
const _hoisted_9 = { class: "card-title" }
const _hoisted_10 = { class: "card-address" }
const _hoisted_11 = { class: "card-address__text" }
const _hoisted_12 = { class: "card-meta" }
const _hoisted_13 = { class: "text-center" }

import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import AppTab from "@/shared/components/ui/AppTab.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppCard from "@/shared/components/ui/AppCard.vue";
import LoadingState from "@/shared/components/feedback/LoadingState.vue";
import { fetchVendorInquiries } from "@/shared/services/inquiries.api";
import AppButtonLoadMore from "@/shared/components/ui/AppButtonLoadMore.vue";
import { isVendorMode } from "@/shared/utils/appMode";

type InquiryItem = {
  id: number;
  inquiry_no: string;
  date: string;
  quotes_count: number;
  status: string;
  customer: {
    name: string;
    avatar: string;
  };
  address: string;
};

type InquiryApiRecord = Record<string, unknown>;
type InquiryQueryParams = Record<string, string>;
type InquiryListMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'InquiryPage',
  setup(__props) {

const tabs = [
  { label: "New", value: "new" },
  { label: "Quoted", value: "quoted" },
  { label: "Accepted", value: "accepted" },
];
const activeTab = ref("new");
const searchQuery = ref("");
const isLoading = ref(false);
const isLoadingMore = ref(false);
const errorMessage = ref("");
const router = useRouter();

const inquiries = ref<InquiryItem[]>([]);
const pagination = ref<InquiryListMeta>({
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
});
const hasMore = ref(false);
let loadRequestId = 0;
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const statusStyleMap: Record<string, { icon: string; iconTone: string }> = {
  new: { icon: "mdi-bell-outline", iconTone: "card-icon--sky" },
  quoted: { icon: "mdi-file-document-outline", iconTone: "card-icon--peach" },
  accepted: { icon: "mdi-check-decagram-outline", iconTone: "card-icon--mint" },
  completed: { icon: "mdi-check-circle-outline", iconTone: "card-icon--amber" },
};

function statusStyle(status: string) {
  const raw = (status || "").toString().trim().toLowerCase();
  return statusStyleMap[raw] || statusStyleMap.new;
}

function asString(value: unknown, fallback = "") {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return fallback;
}

function asNumber(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function normalizeInquiry(item: InquiryApiRecord): InquiryItem {
  return {
    id: asNumber(item.id, 0),
    inquiry_no: asString(item.inquiry_no, "-"),
    date: asString(item.date || item.created_at || item.updated_at, "-"),
    quotes_count: asNumber(item.quotes_count ?? 0, 0),
    status: asString(item.status, "new"),
    customer: {
      name: asString((item.customer as any)?.name ?? (item as any)?.customer_name, "-"),
      avatar: asString((item.customer as any)?.avatar, ""),
    },
    address: asString(item.address, "-"),
  };
}

function extractInquiryList(payload: unknown): InquiryApiRecord[] {
  if (Array.isArray(payload)) {
    return payload as InquiryApiRecord[];
  }

  const wrapped = payload as { data?: unknown; inquiries?: unknown };
  if (Array.isArray(wrapped?.data)) {
    return wrapped.data as InquiryApiRecord[];
  }
  if (Array.isArray(wrapped?.inquiries)) {
    return wrapped.inquiries as InquiryApiRecord[];
  }

  return [];
}

async function loadInquiries() {
  const requestId = ++loadRequestId;
  const append = pagination.value.current_page > 0 && isLoadingMore.value;
  const nextPage = append ? pagination.value.current_page + 1 : 1;

  if (append) {
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
  }
  errorMessage.value = "";

  try {
    const params: InquiryQueryParams = {
      per_page: "10",
      page: String(nextPage),
    };
    const query = searchQuery.value.trim();
    if (query) params.search = query;
    if (activeTab.value !== "all") params.status = activeTab.value;
    if (isVendorMode()) {
      params.is_vendor = "true";
    }

    const response = await fetchVendorInquiries(params);
    if (requestId !== loadRequestId) return;

    const normalized = extractInquiryList(response).map(normalizeInquiry);
    inquiries.value = append ? [...inquiries.value, ...normalized] : normalized;

    const meta = extractMeta(response);
    if (meta) {
      pagination.value = meta;
      hasMore.value = meta.current_page < meta.last_page;
    } else {
      pagination.value = {
        current_page: nextPage,
        last_page: nextPage,
        per_page: 10,
        total: inquiries.value.length,
      };
      hasMore.value = false;
    }
  } catch (error) {
    if (requestId !== loadRequestId) return;
    console.error("Failed to load inquiries", error);
    errorMessage.value = "Unable to load inquiries right now.";
    if (!append) {
      inquiries.value = [];
      pagination.value = { current_page: 1, last_page: 1, per_page: 10, total: 0 };
      hasMore.value = false;
    }
  } finally {
    if (requestId !== loadRequestId) return;
    if (append) {
      isLoadingMore.value = false;
    } else {
      isLoading.value = false;
    }
  }
}

function extractMeta(payload: unknown): InquiryListMeta | null {
  const wrapped = payload as { meta?: Record<string, unknown> };
  if (!wrapped?.meta) return null;
  const meta = wrapped.meta;
  const currentPage = Number(meta.current_page || 1);
  const lastPage = Number(meta.last_page || 1);
  const perPage = Number(meta.per_page || 10);
  const total = Number(meta.total || 0);

  return {
    current_page: Number.isNaN(currentPage) ? 1 : currentPage,
    last_page: Number.isNaN(lastPage) ? 1 : lastPage,
    per_page: Number.isNaN(perPage) ? 10 : perPage,
    total: Number.isNaN(total) ? 0 : total,
  };
}

function handleOpen(id: number) {
  router.push({ name: "vendor.inquiry.detail", params: { id } });
}

function statusClass(status: string) {
  return `card-status--${status.toLowerCase()}`;
}

function loadMore() {
  if (isLoadingMore.value || !hasMore.value) return;
  isLoadingMore.value = true;
  loadInquiries();
}

onMounted(loadInquiries);

watch(activeTab, () => {
  isLoadingMore.value = false;
  loadInquiries();
});

watch(searchQuery, () => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    isLoadingMore.value = false;
    loadInquiries();
  }, 350);
});

onBeforeUnmount(() => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
});

return (_ctx: any,_cache: any) => {
  const _component_v_img = _resolveComponent("v-img")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(AppTab, {
      modelValue: activeTab.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeTab).value = $event)),
      items: tabs,
      columns: 3
    }, null, 8, ["modelValue"]),
    _createElementVNode("div", null, [
      _createVNode(AppTextField, {
        modelValue: searchQuery.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
        "prepend-inner-icon": "mdi-magnify",
        "hide-details": "",
        placeholder: "Search..."
      }, null, 8, ["modelValue"])
    ]),
    _createElementVNode("p", _hoisted_2, _toDisplayString(pagination.value.total > 0 ? `${inquiries.value.length} of ${pagination.value.total} results` : `${inquiries.value.length} results`), 1),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(errorMessage.value), 1))
      : _createCommentVNode("", true),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(LoadingState, {
            loading: isLoading.value,
            message: "Loading inquiries...",
            "spinner-size": 24,
            "spinner-width": 3
          }, null, 8, ["loading"])
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(inquiries.value, (item) => {
            return (_openBlock(), _createBlock(AppCard, {
              key: item.id,
              class: "inquiry-card",
              unstyled: "",
              role: "button",
              tabindex: "0",
              onClick: ($event: any) => (handleOpen(item.id))
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["card-icon", statusStyle(item.status).iconTone])
                  }, [
                    (item.customer.avatar)
                      ? (_openBlock(), _createBlock(_component_v_avatar, {
                          key: 0,
                          size: "42"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_img, {
                              src: item.customer.avatar,
                              alt: item.customer.name,
                              cover: ""
                            }, null, 8, ["src", "alt"])
                          ]),
                          _: 2
                        }, 1024))
                      : (_openBlock(), _createBlock(_component_v_icon, {
                          key: 1,
                          size: "20"
                        }, {
                          default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                            _createTextVNode("mdi-account-circle-outline", -1)
                          ]))]),
                          _: 1
                        }))
                  ], 2),
                  _createElementVNode("div", _hoisted_7, [
                    (item.inquiry_no)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(item.inquiry_no), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("p", _hoisted_9, _toDisplayString(item.customer.name), 1),
                    _createElementVNode("div", _hoisted_10, [
                      _createVNode(_component_v_icon, {
                        size: "14",
                        class: "card-address__icon"
                      }, {
                        default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                          _createTextVNode("mdi-map-marker-outline", -1)
                        ]))]),
                        _: 1
                      }),
                      _createElementVNode("span", _hoisted_11, _toDisplayString(item.address), 1)
                    ]),
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("span", null, _toDisplayString(item.date), 1)
                    ])
                  ])
                ]),
                _createElementVNode("span", {
                  class: _normalizeClass(["card-status", statusClass(item.status)])
                }, _toDisplayString(item.status), 3)
              ]),
              _: 2
            }, 1032, ["onClick"]))
          }), 128))
        ])),
    _createElementVNode("div", _hoisted_13, [
      (hasMore.value)
        ? (_openBlock(), _createBlock(AppButtonLoadMore, {
            key: 0,
            loading: isLoadingMore.value,
            onClick: loadMore
          }, {
            default: _withCtx(() => [
              _cache[5] || (_cache[5] = _createTextVNode(" Load more ", -1)),
              _createVNode(_component_v_icon, { size: "18" }, {
                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                  _createTextVNode("mdi-chevron-double-down", -1)
                ]))]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["loading"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})